import {
    Modal, Box, Typography, TextField, Button
} from "@mui/material";
import { DropDown, SearchBar } from "../components/textField";
import EnhancedTable from "../components/newTable";
import { useState, useEffect, useMemo } from "react";
import { content } from "../utils/data";
import { courseHistoryTrack } from "../api/serverApi";
import Spinner from "../components/spinner";
import StatusModal from "../components/statusModal";
import { useCookies } from "react-cookie";
import { useMsal } from "@azure/msal-react";
import { getValidAccessToken } from "../services/garphService";
import * as microsoftTeams from "@microsoft/teams-js";
import TruncatedText from "./truncatedText";

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2
};

const isInTeams = () => {
    try {
        return window.self !== window.top && window.name === "embedded-page-container";
    } catch {
        return false;
    }
};

const CourtHistoryModal = ({ open, onClose, os_number }) => {
    const [filteredRows, setFilteredRows] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setModalOpen] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');
    const { instance, accounts } = useMsal();
    const [cookies] = useCookies(["accessToken"]);

    useEffect(() => {
        if (open && os_number) {
            const fetchData = async () => {
                setLoading(true);
                setFilteredRows([]); // ✅ Clear stale data before fetch

                try {
                    let token;
                    if (isInTeams()) {
                        token = await new Promise((resolve, reject) => {
                            microsoftTeams.authentication.getAuthToken({
                                successCallback: (t) => resolve(t),
                                failureCallback: (err) => reject(err),
                                resources: ["https://graph.microsoft.com"],
                            });
                        });
                    } else {
                        token = await getValidAccessToken(instance, accounts);
                    }
                    const res = await courseHistoryTrack(os_number, token); // 🔁 API call with token

                    if (res.data) {
                        setFilteredRows({
                            ...res.data,
                            case_logs: Array.isArray(res.data.case_logs) ? res.data.case_logs : []
                        });
                    } else {
                        setFilteredRows({
                            os_number: '',
                            os_date: '',
                            summary: '',
                            status: '',
                            next_hearing_date: '',
                            closure_date: '',
                            area_under_litigation_acre: '',
                            area_under_litigation_gunta: '',
                            foundation_role: '',
                            plaintiff: '',
                            defendant: '',
                            case_logs: []
                        });
                    }

                } catch (err) {
                    const message = err.response?.data?.detail || 'An unexpected error occurred';
                    setResponseMessage(message);
                    setModalOpen(true);
                    setFilteredRows({ case_logs: [], os_number: '', os_date: '' });
                } finally {
                    setLoading(false);
                }
            };

            fetchData(); // ✅ Trigger the async call
        }
    }, [open, os_number, instance, accounts]);

    const rowsWithActions = useMemo(() => {
        if (!filteredRows || !Array.isArray(filteredRows.case_logs)) return [];

        const formatDate = (dateString) => {
            const date = new Date(dateString);
            return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
        };

        const lowerSearch = searchTerm.toLowerCase();

        return filteredRows.case_logs
            .map(({ old_status, new_status, updated_by, updated_at }) => ({
                old_status,
                new_status,
                updated_by,
                updated_at: formatDate(updated_at),
            }))
            .filter((row) =>
                [row.old_status, row.new_status, row.updated_by, row.updated_at].some(
                    (field) => String(field || "").toLowerCase().includes(lowerSearch)
                )
            );
    }, [filteredRows, searchTerm]);

    return (
        <>
            <Modal open={open} onClose={onClose}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" gutterBottom>
                        Court Case History &gt; {os_number}
                    </Typography>

                    {/* <div className="mb-4">
                        <SearchBar value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                    </div> */}

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                        <div>
                            <strong>Original Suite No:</strong> <span>{filteredRows.os_number}</span>
                        </div>
                        <div>
                            <strong>Original Suite Date:</strong> {filteredRows.os_date}
                        </div>
                        <div>
                            <strong>Status:</strong> {filteredRows.status}
                        </div>
                        <div>
                            <strong>Area in Acre:</strong> {filteredRows.area_under_litigation_acre}
                        </div>
                        <div>
                            <strong>Area in Gunta:</strong> {filteredRows.area_under_litigation_gunta}
                        </div>
                        <div>
                            <strong>Foundation Role:</strong> {filteredRows.foundation_role}
                        </div>
                        <div>
                            <strong>Next Hearing Date:</strong> {filteredRows.next_hearing_date}
                        </div>
                        <div>
                            <strong>Closure Date:</strong> {filteredRows.closure_date}
                        </div>
                        <div>
                            <strong>Summary:</strong> <TruncatedText text={filteredRows.summary} maxLines={2} />
                            {/* {filteredRows.summary} */}
                        </div>
                    </div>

                    <EnhancedTable
                        rows={rowsWithActions}
                        header={content.historyTrack}
                    />

                    {loading && <Spinner />}

                    <StatusModal
                        isOpen={isModalOpen}
                        onClose={() => setModalOpen(false)}
                        message={responseMessage}
                        isSuccess={isSuccess}
                    />
                </Box>
            </Modal>
        </>
    );
};

export default CourtHistoryModal;
